import { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUnsavedChanges } from '@/contexts/UnsavedChangesContext';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const NavigationGuard = () => {
  const { hasUnsavedChanges, markClean } = useUnsavedChanges();
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [pendingPath, setPendingPath] = useState<string | null>(null);

  // Intercept all <a> link clicks in capture phase (before React Router processes them)
  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const handleClick = (e: MouseEvent) => {
      const anchor = (e.target as HTMLElement).closest('a[href]');
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('http') || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('blob:') || href.startsWith('data:')) return;
      if (anchor.hasAttribute('download') || anchor.getAttribute('target') === '_blank') return;
      // Extract pathname (ignore query/hash)
      const targetPath = href.split('?')[0].split('#')[0];
      if (targetPath === location.pathname) return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      setPendingPath(targetPath);
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [hasUnsavedChanges, location.pathname]);

  // Browser tab close / refresh guard
  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);

  const handleProceed = useCallback(() => {
    if (pendingPath) {
      markClean();
      const path = pendingPath;
      setPendingPath(null);
      navigate(path);
    }
  }, [pendingPath, markClean, navigate]);

  const handleCancel = useCallback(() => {
    setPendingPath(null);
  }, []);

  if (!pendingPath) return null;

  const isAr = lang === 'ar';

  return (
    <AlertDialog open>
      <AlertDialogContent dir={isAr ? 'rtl' : 'ltr'}>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {isAr ? 'تغييرات غير محفوظة' : 'Unsaved Changes'}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {isAr
              ? 'لديك تغييرات لم يتم حفظها. هل تريد المتابعة بدون حفظ؟'
              : 'You have unsaved changes. Do you want to continue without saving?'}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className={isAr ? 'flex-row-reverse gap-2' : ''}>
          <AlertDialogCancel onClick={handleCancel}>
            {isAr ? 'إلغاء' : 'Cancel'}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleProceed}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isAr ? 'متابعة بدون حفظ' : 'Continue without saving'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default NavigationGuard;
